import { Action } from 'store/store';
import {
    ReceivedMessageState,
    ReceivedMessageStatus,
    ReceiveMessageErrorStatus,
    SendMessageErrorStatus,
    SendMessageStatus,
} from 'embedded/api/store/EmbeddedApiState';
import { Identity } from 'embedded/api/out/Identity';

export enum EmbeddedApiActionType {
    EMBEDDED_API_RECEIVE_MESSAGE = 'EMBEDDED_API_RECEIVE_MESSAGE',
    EMBEDDED_API_UPDATE_RECEIVED_MESSAGE = 'EMBEDDED_API_UPDATE_RECEIVED_MESSAGE',
    EMBEDDED_API_SEND_MESSAGE = 'EMBEDDED_API_SEND_MESSAGE',
}

export type EmbeddedApiReceiveMessageAction<TMessagePayload> = Action<
    EmbeddedApiActionType.EMBEDDED_API_RECEIVE_MESSAGE,
    Omit<ReceivedMessageState<TMessagePayload>, 'seq'>
>;

export const embeddedApiReceiveMessageAction: <TMessagePayload>(
    uid: string,
    topic: string,
    payload: TMessagePayload,
    status: ReceivedMessageStatus,
    errorStatus?: ReceiveMessageErrorStatus,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    details?: any
) => EmbeddedApiReceiveMessageAction<TMessagePayload> = (
    uid,
    topic,
    payload,
    status,
    errorStatus,
    details
) => ({
    type: EmbeddedApiActionType.EMBEDDED_API_RECEIVE_MESSAGE,
    payload: {
        uid,
        topic,
        payload,
        receivedTime: new Date(),
        details,
        status,
        errorStatus,
        updatedTime: new Date(),
    },
});

export interface EmbeddedApiUpdateReceivedMessagePayload {
    uid: string;
    status: ReceivedMessageStatus;
    errorStatus?: ReceiveMessageErrorStatus;
    error?: Error;
    updatedTime: Date;
}

export type EmbeddedApiUpdateReceivedMessageStatusAction = Action<
    EmbeddedApiActionType.EMBEDDED_API_UPDATE_RECEIVED_MESSAGE,
    EmbeddedApiUpdateReceivedMessagePayload
>;

export const embeddedApiUpdateReceivedMessageStatus: (
    uid: string,
    status: ReceivedMessageStatus,
    errorStatus?: ReceiveMessageErrorStatus,
    error?: Error
) => EmbeddedApiUpdateReceivedMessageStatusAction = (
    uid,
    status,
    errorStatus,
    error
) => {
    return {
        type: EmbeddedApiActionType.EMBEDDED_API_UPDATE_RECEIVED_MESSAGE,
        payload: { uid, status, errorStatus, error, updatedTime: new Date() },
    };
};

export interface EmbeddedApiSendMessagePayload<TPayload> {
    identity: Identity;
    topic: string;
    status: SendMessageStatus;
    errorStatus?: SendMessageErrorStatus;
    message?: TPayload;
    time: Date;
    error?: Error;
}

export type EmbeddedApiSendMessageAction<TPayload> = Action<
    EmbeddedApiActionType.EMBEDDED_API_SEND_MESSAGE,
    EmbeddedApiSendMessagePayload<TPayload>
>;

export const embeddedApiSendMessageAction: <TPayload>(
    identity: Identity,
    topic: string,
    status: SendMessageStatus,
    errorStatus?: SendMessageErrorStatus,
    payload?: TPayload,
    error?: Error
) => EmbeddedApiSendMessageAction<TPayload> = (
    identity,
    topic,
    status,
    errorStatus,
    payload,
    error
) => {
    return {
        type: EmbeddedApiActionType.EMBEDDED_API_SEND_MESSAGE,
        payload: {
            identity,
            topic,
            status,
            errorStatus,
            payload,
            error,
            time: new Date(),
        },
    };
};

export type EmbeddedApiAction =
    | EmbeddedApiReceiveMessageAction<unknown>
    | EmbeddedApiUpdateReceivedMessageStatusAction
    | EmbeddedApiSendMessageAction<unknown>;
